import { useRef, useState } from "react";
import { Paperclip, X, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";

export const API = process.env.REACT_APP_BACKEND_URL;

export const STATUS_STYLES = {
  open: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  pending: "bg-yellow-500/15 text-yellow-300 border-yellow-500/30",
  review: "bg-purple-500/15 text-purple-300 border-purple-500/30",
  response_sent: "bg-orange-500/15 text-orange-300 border-orange-500/30",
  completed: "bg-green-500/15 text-green-300 border-green-500/30",
  closed: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",
};

export const statusLabel = (s) => (s || "").replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase());
export const fmtDate = (iso) => new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
export const shortAddr = (a) => (a ? `${a.slice(0, 6)}…${a.slice(-4)}` : "");

export const StatusBadge = ({ status, testid }) => (
  <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-mono uppercase ${STATUS_STYLES[status] || ""}`} data-testid={testid}>
    {statusLabel(status)}
  </span>
);

// Upload picker: uploads immediately, returns file ids via onChange.
export const AttachmentPicker = ({ headers, files, onChange, testid = "support-attach" }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = async (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    setBusy(true);
    const added = [];
    for (const f of list) {
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name} is over 10 MB`); continue; }
      const fd = new FormData();
      fd.append("file", f);
      const r = await fetch(`${API}/api/support/upload`, { method: "POST", headers, body: fd });
      if (r.ok) added.push(await r.json()); else toast.error(`Upload failed: ${f.name}`);
    }
    onChange([...files, ...added].slice(0, 10));
    setBusy(false);
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <input ref={ref} type="file" multiple className="hidden" onChange={pick} data-testid={`${testid}-input`}
        accept="image/*,.pdf,.txt,.csv,.doc,.docx,.xls,.xlsx,.json,.zip" />
      <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid={`${testid}-button`}
        className="inline-flex items-center gap-2 rounded-full border border-white/15 px-3 py-1.5 text-xs text-white/80 hover:border-primary hover:text-primary transition-colors">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />}Attach file / image
      </button>
      {files.map((f) => (
        <span key={f.id} className="inline-flex items-center gap-1 rounded-full bg-white/[0.06] px-3 py-1 text-xs" data-testid={`${testid}-file-${f.id}`}>
          {f.filename}
          <button type="button" onClick={() => onChange(files.filter((x) => x.id !== f.id))} data-testid={`${testid}-remove-${f.id}`}>
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
    </div>
  );
};

const AttachmentView = ({ a }) => {
  const url = `${API}${a.url}`;
  if ((a.content_type || "").startsWith("image/")) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" data-testid={`support-attachment-${a.id}`}>
        <img src={url} alt={a.filename} className="h-28 w-28 rounded-lg border border-white/10 object-cover" />
      </a>
    );
  }
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" data-testid={`support-attachment-${a.id}`}
      className="inline-flex items-center gap-2 rounded-lg border border-white/10 px-3 py-2 text-xs hover:border-primary">
      <FileText className="h-4 w-4 text-primary" />{a.filename}
    </a>
  );
};

export const MessageThread = ({ messages, viewerAddr }) => (
  <div className="space-y-4" data-testid="support-thread">
    {messages.map((m) => (
      <div key={m.id} data-testid={`support-msg-${m.id}`}
        className={`rounded-xl border p-4 ${m.author === "admin" ? "border-primary/30 bg-primary/[0.05] ml-6" : "border-white/10 bg-white/[0.02] mr-6"}`}>
        <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] font-mono text-muted-foreground">
          <span className={m.author === "admin" ? "text-primary" : ""}>
            {m.author === "admin" ? "STXOSHI Support" : `Wallet ${shortAddr(m.address)}`}
          </span>
          <span>{fmtDate(m.created_at)}</span>
        </div>
        {m.message && <p className="mt-2 whitespace-pre-wrap text-sm text-white/90">{m.message}</p>}
        {m.attachments?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">{m.attachments.map((a) => <AttachmentView key={a.id} a={a} addr={viewerAddr} />)}</div>
        )}
      </div>
    ))}
  </div>
);
